<template>
  <div class="relative" ref="rootEl">
    <button
      type="button"
      @click="open = !open"
      :class="open
        ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-200'
        : 'border-gray-300 dark:border-white/10 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:border-purple-500/50'"
      class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium shadow-sm transition-colors"
    >
      <span class="text-[10px] uppercase tracking-wider text-gray-400">{{ label }}</span>
      <span class="text-gray-900 dark:text-white">{{ summary }}</span>
      <span v-if="modelValue.length" class="px-1.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-700 dark:text-purple-200">
        {{ modelValue.length }}
      </span>
      <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute left-0 top-full mt-2 z-30 w-72 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl"
      >
        <div class="px-3 py-2 border-b border-gray-200 dark:border-white/10">
          <input
            v-model="search"
            type="search"
            :placeholder="`Search ${label.toLowerCase()}…`"
            class="glass-input w-full px-3 py-1.5 text-xs"
          />
        </div>
        <div class="max-h-64 overflow-y-auto py-1">
          <p v-if="!filtered.length" class="px-3 py-3 text-xs text-gray-500 italic">
            {{ options.length ? 'No matches.' : 'Nothing to choose from yet.' }}
          </p>
          <label
            v-for="opt in filtered"
            :key="opt"
            class="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
          >
            <input
              type="checkbox"
              :value="opt"
              :checked="modelValue.includes(opt)"
              @change="toggle(opt)"
              class="rounded"
            />
            <span class="font-mono text-xs truncate">{{ opt }}</span>
          </label>
        </div>
        <div v-if="modelValue.length" class="px-3 py-2 border-t border-gray-200 dark:border-white/10 flex justify-end">
          <button
            type="button"
            @click="$emit('update:modelValue', [])"
            class="text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-red-600"
          >Clear</button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  label: string
  modelValue: string[]
  options?: string[]
}>()
const emit = defineEmits<{ (e: 'update:modelValue', value: string[]): void }>()

const open = ref(false)
const search = ref('')
const rootEl = ref<HTMLElement | null>(null)

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return (props.options || []).filter(o => !q || o.toLowerCase().includes(q))
})

const summary = computed(() => {
  const n = props.modelValue.length
  if (!n) return 'All'
  if (n === 1) return props.modelValue[0]
  return `${n} selected`
})

function toggle(opt: string) {
  const set = new Set(props.modelValue)
  if (set.has(opt)) set.delete(opt); else set.add(opt)
  emit('update:modelValue', Array.from(set))
}

function onDocumentClick(e: MouseEvent) {
  if (!open.value || !rootEl.value) return
  if (e.target instanceof Node && rootEl.value.contains(e.target)) return
  open.value = false
}
onMounted(() => document.addEventListener('mousedown', onDocumentClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentClick))
</script>
